import React, { useState, useEffect } from "react";
import { useLanguage } from "@/App";
import { Menu, X } from "lucide-react";
import Logo from "@/components/Logo";

const Navigation = () => {
  const { language, toggleLanguage, t } = useLanguage();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { href: "#showcase", label: "Portfolio" },
    { href: "#ai-automation", label: language === "nl" ? "AI Automatisering" : "AI Automation" },
    { href: "#services", label: t("nav.services") },
    { href: "#extra-services", label: language === "nl" ? "Extra Diensten" : "Extra Services" },
    { href: "#maintenance", label: "Hosting" },
    { href: "#pricing", label: t("nav.pricing") },
    { href: "#contact", label: t("nav.contact") },
  ];

  return (
    <nav
      data-testid="navigation"
      className={`fixed top-0 w-full z-50 transition-all duration-300 ${
        isScrolled
          ? "bg-black/90 backdrop-blur-xl border-b border-[#FF6B00]/20"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <a href="/" data-testid="nav-logo" className="flex-shrink-0">
            <Logo size="default" />
          </a>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                data-testid={`nav-link-${link.href.replace("#", "")}`}
                className="text-sm font-medium text-zinc-400 hover:text-white transition-colors"
              >
                {link.label}
              </a>
            ))}
          </div>

          {/* Right side */}
          <div className="hidden md:flex items-center gap-6">
            {/* Language Switcher */}
            <button
              onClick={toggleLanguage}
              data-testid="language-switcher"
              className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-zinc-500 hover:text-white transition-colors"
            >
              <span className={language === "en" ? "text-white" : ""}>EN</span>
              <span className="text-zinc-600">/</span>
              <span className={language === "nl" ? "text-white" : ""}>NL</span>
            </button>

            {/* CTA Button */}
            <a
              href="#contact"
              data-testid="nav-cta"
              className="bg-[#FF6B00] text-white px-6 py-3 text-sm font-bold tracking-wide hover:bg-[#FF6B00]/90 transition-all"
            >
              {t("nav.getStarted")}
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            data-testid="mobile-menu-button"
            className="md:hidden text-white p-2"
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div
            data-testid="mobile-menu"
            className="md:hidden bg-[#0A0A0A] border-t border-white/10 py-6"
          >
            <div className="flex flex-col gap-4">
              {navLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  onClick={() => setIsMobileMenuOpen(false)}
                  data-testid={`mobile-nav-link-${link.href.replace("#", "")}`}
                  className="text-lg font-medium text-zinc-400 hover:text-white transition-colors px-4 py-2"
                >
                  {link.label}
                </a>
              ))}
              <div className="flex items-center justify-between px-4 pt-4 border-t border-white/10">
                <button
                  onClick={toggleLanguage}
                  data-testid="mobile-language-switcher"
                  className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500"
                >
                  <span className={language === "en" ? "text-white" : ""}>EN</span>
                  <span className="text-zinc-600 mx-2">/</span>
                  <span className={language === "nl" ? "text-white" : ""}>NL</span>
                </button>
                <a
                  href="#contact"
                  onClick={() => setIsMobileMenuOpen(false)}
                  data-testid="mobile-nav-cta"
                  className="bg-[#FF6B00] text-white px-6 py-3 text-sm font-bold"
                >
                  {t("nav.getStarted")}
                </a>
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navigation;
